<template>
  <ModalUi class="approve-modal first-level-modal" @closeModal="toggleModals('toggleModals')">
    <div class="modal-wrap">
      <div class="modal-block">
        <div class="modal-title">
          <div class="text-block row">
            <h3>Подтверждение заказа</h3>
            <p class="subtitle" v-html="subtitle" />
          </div>
        </div>
        <div class="modal-content">
          <div class="phone-approve">
            <div class="order-input">
              <PhoneInput ref="phoneInput" :phone="phone" @update:phone="phone = $event" />
            </div>
            <div class="cooldown-btn">
              <button
                type="button"
                @click="trySendCode"
                :disabled="!$refs.phoneInput?.isValid || timeLeft > 0 || approveUser.isValided"
              >
                <span v-if="!timeLeft">{{ buttonText }}</span>
                <Cooldown v-else :timeLeft="timeLeft" />
              </button>
            </div>
          </div>

          <SlideTransition>
            <div class="approve-code" v-if="approveUser.status !== -1">
              <input
                :disabled="approveUser.isValided"
                type="tel"
                v-model="code"
                @focus="codeHasFocused = true"
                ref="codeField"
                :placeholder="codePlaceholder"
                :class="{
                  'invalid-feedback': getValidationError('code') || !approveUser.isValided,
                  'is-valid': approveUser.isValided,
                }"
                @input="tryCheckCode"
                v-show="approveUser.message"
              />
            </div>
          </SlideTransition>
        </div>
        <div class="modal-footer">
          <div class="approve-messages">
            <ValidationView :error-messages="codeLengthErr" />
            <div
              :class="approveUser.status === 0 ? 'valid-text' : 'invalid-text'"
              style="display: block"
              v-if="approveUser.message"
            >
              {{ approveUser.message }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </ModalUi>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import PhoneInput from '@features/PhoneInputWithValidation.vue'
import { GENERAL_STORE } from '@apps/order-form/src/store'
import ModalUi from '@features/UI/ModalUi.vue'
import ValidationMixin from '@mixins/validationMixin'
import Cooldown from '@features/UI/Cooldown.vue'
import SlideTransition from '../Transitions/SlideTransition.vue'

export default {
  name: 'PhoneApproveModal',
  components: {
    SlideTransition,
    PhoneInput,
    ModalUi,
    Cooldown,
  },
  mixins: [ValidationMixin],
  data() {
    return {
      codeHasFocused: false,
    }
  },
  validations() {
    return {
      code: {
        reqLength: this.reqLength(4),
      },
    }
  },
  watch: {
    isCooldown(val) {
      if (!val) this.isCooldownEnded = true
    },
  },
  computed: {
    ...mapState(GENERAL_STORE, ['approveUser', 'order']),
    ...mapGetters(GENERAL_STORE, ['buildedOrder']),

    isCooldownEnded: {
      get() {
        return this.$store.state[GENERAL_STORE].isCooldownEnded
      },
      set(value) {
        this.$store.commit(`${GENERAL_STORE}/updateState`, [{ key: 'isCooldownEnded', value }])
      },
    },

    successfulRequestsAmount: {
      get() {
        return this.$store.state[GENERAL_STORE].successfulRequestsAmount
      },
      set(value) {
        this.$store.commit(`${GENERAL_STORE}/updateState`, [
          { key: 'successfulRequestsAmount', value },
        ])
      },
    },

    isAutoCall() {
      return this.successfulRequestsAmount <= 1 && !this.isCooldownEnded
    },

    isSmsCheck() {
      return this.successfulRequestsAmount > 1
    },

    isCooldown() {
      return this.timeLeft > 0
    },

    subtitle() {
      if (this.isAutoCall) {
        return `Для подтверждения заказа укажите ваш мобильный телефон, на него будет совершен <b>автодозвон</b>. Не берите трубку.
Введите последние 4 цифры входящего звонка и заказ запустится.`
      }

      return `Для подтверждения заказа укажите ваш мобильный телефон, на него будет отправлено SMS с кодом.
Введите код и заказ запустится.`
    },

    buttonText() {
      if (this.isAutoCall) {
        return 'Подтвердить'
      }

      return 'Подтвердить по SMS'
    },

    codePlaceholder() {
      if (this.isSmsCheck) {
        return 'Код из SMS'
      }

      return 'Последние 4 цифры входящего номера'
    },

    codeLengthErr() {
      const field = this.getField('code')

      return (
        this.codeHasFocused &&
        this.approveUser.status !== -1 &&
        this.approveUser.message &&
        field.$invalid &&
        field.reqLength.$message
      )
    },

    phone: {
      get() {
        return this.approveUser.phoneNumber
      },
      set(value) {
        this.updateApproveUserNumber(value)
      },
    },

    code: {
      get() {
        return this.approveUser.code
      },
      set(value) {
        this.updateApproveUserCode(value)
      },
    },
    timeLeft: {
      get() {
        return this.approveUser.timeLeft
      },
      set(value) {
        this.updateApproveUserTimeLeft(value)
      },
    },
  },
  methods: {
    ...mapMutations(GENERAL_STORE, [
      'updateApproveUserNumber',
      'updateApproveUserCode',
      'toggleModals',
      'updateApproveUserTimeLeft',
      'updateState',
    ]),
    ...mapActions(GENERAL_STORE, [
      'approveUserPhone',
      'approveUserSendCode',
      'approveUserCheckCode',
      'setCountDown',
    ]),

    tryCheckCode() {
      if (!this.codeLengthErr) {
        this.approveUserCheckCode()
      }
    },
    async trySendCode() {
      if (!this.$refs.phoneInput.v$.$invalid && !this.approveUser.isFetching) {
        this.setCountDown()
        await this.approveUserSendCode()

        if (this.approveUser.status === 0) {
          this.successfulRequestsAmount += 1
        }
      }
    },
  },
  created() {
    this.phone = this.buildedOrder.Dots[0].phone.slice(2, 16)
  },
}
</script>

<style scoped lang="scss">
.approve-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.phone-approve {
  gap: 10px;

  @media (max-width: 430px) {
    display: grid !important;

    .order-input {
      width: 100% !important;
    }
  }
}

.modal-content {
  max-width: 360px;
}
</style>
